// Words too generic to search on their own -- "mens"/"womens" in particular
// are loose enough matches (Shopify's search treats "mens" as effectively a
// substring of "womens") that trying one alone can pull back the wrong
// gender entirely. They're still included when every word is searched
// together, just never tried in isolation.
const GENERIC_WORDS = new Set(['mens', 'womens', 'unisex', 'kids', 'boys', 'girls', 'junior', 'adult'])

class ErrorPageRecommendations extends HTMLElement {
  async connectedCallback() {
    this.spinner = this.querySelector('[data-recommendations-spinner]')
    this.headerEl = this.querySelector('[data-recommendations-header]')
    this.heading = this.querySelector('[data-recommendations-heading]')
    this.grid = this.querySelector('[data-recommendations-grid]')
    this.searchUrl = this.dataset.searchUrl || '/search'

    // Liquid's `request.path` always reports "/404" on the error template,
    // never the URL that was actually requested -- that's a Shopify
    // limitation, not something fixable server-side. window.location still
    // has the real broken URL, so the guess has to happen here instead.
    const candidates = this.guessWordsFromPath(window.location.pathname)

    if (!candidates.length) return

    // Reveal the block now (spinner only -- the grid/heading stay hidden
    // until there's something to show) so the hero above doesn't have to
    // stretch to make room once results land.
    this.hidden = false

    let initial = null
    for (const query of this.buildQueries(candidates)) {
      initial = await this.fetchResults(query)
      if (initial && initial.hasResults) break
    }

    if (!initial || !initial.hasResults) {
      this.hidden = true
      return
    }

    let query = initial.term
    let result = initial

    if (initial.type) {
      const typeQuery = `product_type:"${initial.type.replace(/"/g, '')}"`
      const typed = await this.fetchResults(typeQuery)
      if (typed && typed.hasResults) {
        result = typed
        query = typeQuery
      }
    }

    this.show(result.markup, initial.type, query)
  }

  guessWordsFromPath(pathname) {
    if (!pathname.includes('/products/') && !pathname.includes('/collections/')) return []

    const segments = pathname.split('/').filter(Boolean)
    const lastSegment = segments[segments.length - 1] || ''

    return lastSegment.split(/[-_]+/).filter((word) => word.length > 2 && !/^\d+$/.test(word))
  }

  buildQueries(words) {
    const queries = []

    // Most precise first: every word required to match (still gets normal
    // per-word typo tolerance since this isn't boolean/field search syntax)
    // -- this is what correctly narrows "ripcurl mens dawn" down instead of
    // matching on "mens" alone.
    if (words.length > 1) queries.push(words.join(' '))

    const specific = words
      .filter((word) => !GENERIC_WORDS.has(word.toLowerCase()))
      .sort((a, b) => b.length - a.length)

    return queries.concat(specific)
  }

  fetchResults(term) {
    const params = new URLSearchParams({
      q: term,
      type: 'product',
      section_id: 'main-404-recommendations'
    })

    return fetch(`${window.Shopify.routes.root}search?${params.toString()}`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!text) return null

        const doc = new DOMParser().parseFromString(text, 'text/html')
        const typeTag = doc.querySelector('[data-404-recommendations-type]')
        const items = doc.querySelector('[data-404-recommendations-items]')

        let type = ''
        if (typeTag) {
          try {
            type = JSON.parse(typeTag.textContent || '""')
          } catch (error) {
            type = ''
          }
        }

        return {
          term,
          type,
          hasResults: !!items,
          markup: items ? items.innerHTML : ''
        }
      })
      .catch(() => null)
  }

  show(markup, type, query) {
    if (this.spinner) this.spinner.hidden = true
    if (!this.grid) return

    this.grid.innerHTML = markup

    if (this.heading) {
      this.heading.textContent = type ? `${this.dataset.heading} — ${type}` : this.dataset.heading
      this.heading.href = `${this.searchUrl}?q=${encodeURIComponent(query)}&type=product`
    }

    if (this.headerEl) this.headerEl.hidden = false
  }
}

customElements.define('error-page-recommendations', ErrorPageRecommendations)
